import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ApiError } from '../api';
import { adminApi, errorText, type Identity } from './api';
import { MailsPage } from './mails';
import { ProjectPage } from './project';
import { WebinarEditPage } from './webinar-edit';
import { WebinarListPage } from './webinars';

/**
 * Die Verwaltung unter app.<Plattform>/admin. Ein Zugang, ein Projekt (Konzept 3.2) – deshalb gibt es
 * hier keinen Projektumschalter. Seitenwechsel ohne Neuladen über history.pushState.
 */

export function navigate(path: string): void {
  window.history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function usePath(): string {
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const onChange = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onChange);
    return () => window.removeEventListener('popstate', onChange);
  }, []);
  return path;
}

export function AdminApp() {
  const path = usePath();
  const [identity, setIdentity] = useState<Identity | null | undefined>(undefined);

  const refresh = useCallback(() => {
    adminApi
      .me()
      .then(setIdentity)
      .catch(() => setIdentity(null));
  }, []);

  useEffect(refresh, [refresh]);

  useEffect(() => {
    document.title = identity ? `${identity.project.name} – Verwaltung` : 'Evercast – Anmeldung';
  }, [identity]);

  if (identity === undefined) return null;
  if (identity === null) return <LoginPage onLogin={setIdentity} />;

  async function logout() {
    await adminApi.logout().catch(() => undefined);
    setIdentity(null);
    navigate('/admin');
  }

  const webinar = /^\/admin\/webinare\/([0-9a-f-]{36})\/?$/.exec(path);
  let page: React.ReactNode;
  if (path === '/admin/projekt') page = <ProjectPage />;
  else if (path === '/admin/mails') page = <MailsPage />;
  else if (webinar) page = <WebinarEditPage id={webinar[1]!} projectSlug={identity.project.slug} />;
  else page = <WebinarListPage />;

  return (
    <>
      <header className="admin-bar">
        <a className="admin-bar__brand" href="/admin" onClick={(e) => (e.preventDefault(), navigate('/admin'))}>
          {identity.project.name}
        </a>
        <nav className="admin-bar__nav">
          <a href="/admin" className={path === '/admin' || path.startsWith('/admin/webinare') ? 'is-active' : ''} onClick={(e) => (e.preventDefault(), navigate('/admin'))}>
            Webinare
          </a>
          <a href="/admin/mails" className={path === '/admin/mails' ? 'is-active' : ''} onClick={(e) => (e.preventDefault(), navigate('/admin/mails'))}>
            E-Mails
          </a>
          <a href="/admin/projekt" className={path === '/admin/projekt' ? 'is-active' : ''} onClick={(e) => (e.preventDefault(), navigate('/admin/projekt'))}>
            Projekt
          </a>
        </nav>
        <span className="admin-bar__spacer" />
        <span className="muted small">{identity.user.email}</span>
        <button type="button" className="button button--secondary button--small" onClick={logout}>
          Abmelden
        </button>
      </header>
      <main className="admin-main">{page}</main>
    </>
  );
}

function LoginPage({ onLogin }: { onLogin: (identity: Identity) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onLogin(await adminApi.login(email, password));
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setError('E-Mail-Adresse oder Passwort stimmen nicht.');
      else if (e instanceof ApiError && e.status === 429) setError('Zu viele Versuche. Bitte in einer Stunde noch einmal.');
      else setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="page" style={{ maxWidth: 440, paddingTop: 96 }}>
      <div>
        <p className="label label--brand">Evercast</p>
        <h1 style={{ fontSize: 28, marginTop: 8 }}>Anmeldung zur Verwaltung</h1>
      </div>
      <form className="card" onSubmit={submit}>
        <div className="field">
          <label htmlFor="email">E-Mail-Adresse</label>
          <input id="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="password">Passwort</label>
          <input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        {error && <div className="error">{error}</div>}
        <button type="submit" className="button" disabled={busy}>
          {busy ? 'Einen Moment …' : 'Anmelden'}
        </button>
      </form>
    </main>
  );
}
